import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { joiResolver } from "@hookform/resolvers/joi";
import clsx from "clsx";
import dayjs, { Dayjs } from "dayjs";
import { exitReact, formatFullLocation } from "../../../utils";
import TopBarRedesigned from "components/TopBarRedesigned/TopBarRedesigned";
import { Button, Container, MenuItem, Switch } from "@mui/material";
import {
    afterPostJobActions, Country,
    JOB_STATUSES,
    paymentTermsOpts,
    paymentTermsOpts123Dentist,
    paymentMethodOpts,
    rateOptions,
    roles,
    schema,
    timePickerOptions
} from "./PostJob.utils";
import styles from './PostJob.module.scss'
import { CollapsableContainer } from "components/CollapsableContainer";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import SpeedIcon from "@material-ui/icons/Speed";
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { FormCheckboxInput } from "components/Inputs/CheckboxInput";
import { FormRadioInputGroup, RadioInput } from "components/Inputs/RadioInput";
import { usePersistedApi, usePersistedState } from "../../../hooks";
import { entities } from "../../../reduxStore";
import { FormMaterialSelector } from "components/Selector";
import Api from "../../../api";
import { SimpleModal } from "../../../components/Modal";
import { Location } from "../../../components/BillingLocationSelector";
import { ShiftIsLiveModal } from "./components/ShiftIsLiveModal";
import { UpdatePaymentInformationModal } from "./components/InstaBookModal";
import { ErrorModal } from "./components/ErrorModal";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import Popup from "../../../components/Popup";
import { FormMaterialCalendarPicker } from "../../../components/Inputs/MaterialCalendarPicker";
import { HygienistProps, Type, UserAccountProps } from "utils/types";
import useActionApi from "../../../hooks/useActionApi";
import PostJobConfirmation from "../PostJobConfirmationPage";
import { isEmpty } from "lodash";
import { AddPaymentDetailsModal } from './components/AddPaymentDetailsModal';
import { LOCATION_IS_CENTRAL_BOOKED } from '../../../utils/constants';

interface JobProps {
    startDate: string,
    status: number,
    type: number,
    isOrtho?: 0 | 1,
    isRestorative?: 0 | 1,
    paymentTerms: string,
    paymentMethod: number,
    locationId: string,
    requiredLevel: number,
    requiredN95: number,
    Instabook?: boolean,
    proposedRate: string,
    paysW2?: number,
}

export interface JobDataProps {
    job: JobProps,
    shifts:
    {
        shiftDate: string,
        postedStart: string,
        postedEnd: string,
        type: number,
        gapUnpaid: number,
        remindStatus: number
    }[],
    ppe: {}
}

export interface ShiftDataProps {
    country: 0 | 1,
    role: boolean,
    location: Location,
    startDate: string,
    from: string,
    to: string,
    level: number,
    paymentTerms: number,
    proposedRate: number,
    instaBook: boolean,
    ppe: boolean,
    paysW2: number,
}

const PostJob = () => {
    const history = useHistory();
    const {
        date,
        shift,
        role: stateRole,
        location: stateLocation,
        from: stateFrom,
        to: stateTo,
        mode,
        job: editJob
    } = history.location.state as any

    const formattedDate = date ? dayjs(date) : undefined
    const [loader, setLoader] = useState(false)
    const [selectedLocation, setSelectedLocation] = useState<Location>()
    const [isJobPostedModal, setIsJobPostedModal] = useState<boolean>(false)
    const [isJobReadyModal, setIsJobReadyModal] = useState<boolean>(false)
    const [isUpdatePaymentModal, setUpdatePaymentModal] = useState<boolean>(false)
    const [jobId, setJobId] = useState<number>()
    const [availableHygienists, setAvailableHygienists] = useState<any>()
    const [error, setError] = useState<string>('')
    const [data, setData] = useState<any>()
    const [lastJob, setLastJob] = useState<any>()
    const [isOpenPaymentDetailsPopup, setIsOpenPaymentDetailsPopup] = useState<boolean>(false)

    const [locations, locationsLoading, , getLocations] = usePersistedApi({
        apiMethod: Api.getValidLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })

    const [lastJobLoading, , getLastDentistJob] = useActionApi({
        apiMethod: Api.getLastJob,
        onSuccess: (job: any) => {
            setLastJob(job)
        }
    });

    const user = usePersistedState({
        entityName: entities.user,
    })[0] as UserAccountProps

    const country = Number(user?.dentist?.country) || Number(user?.country);

    const [getAvailableHygienistsLoading, , getAvailableHygienists] = useActionApi({
        apiMethod: Api.getAvailableHygienists,
        onSuccess: (availableHygienists: { hygienists?: HygienistProps[] }) => {
            setLoader(false)
            if (availableHygienists?.hygienists?.length) {
                setAvailableHygienists(availableHygienists?.hygienists)
            }
            setIsJobPostedModal(true)
        }
    });

    const {
        control,
        register,
        handleSubmit,
        setValue,
        getValues,
        watch,
        resetField,
        formState: { errors, isValid }
    } = useForm({
        defaultValues: {
            country: user?.dentist?.country,
            role: stateRole,
            location: stateLocation || editJob?.locationId,
            startDate: formattedDate,
            from: stateFrom,
            to: stateTo,
            level: shift?.level || editJob?.requiredLevel,
            paymentTerms: shift?.paymentTerms || editJob?.paymentTerms,
            paymentMethod: shift?.paymentMethod || editJob?.paymentMethod,
            proposedRate: shift?.proposedRate || editJob?.proposedRate,
            instaBook: editJob?.Instabook || shift?.instaBook || false,
            ppe: shift?.ppe || editJob?.ppe || false,
            skills: editJob?.isOrtho ? 'isOrthodonticFlag' : editJob?.isRestorative ? 'isRestorativeFlag' : undefined,
            paysW2: country === Country.USA ? (shift?.paysW2 || editJob?.paysW2 || 2) : undefined,
            downtimeUnpaid: editJob?.shifts?.[0]?.gapUnpaid === 1
        } as any,
        resolver: joiResolver(schema) as any,
        mode: 'onChange',
        reValidateMode: 'onChange'
    });

    useEffect(() => {
        if (country === Country.USA) {
            setValue('paysW2', shift?.paysW2 || editJob?.paysW2 || 2);
        } else {
            resetField('paysW2');
        }
    }, [country, editJob, shift, setValue, resetField]);

    useEffect(() => {
        if (mode === 'edit') {
            console.log('Form errors:', errors);
            if (!errors.ppe && editJob) {
                setValue('ppe', true);
            }
        }
    }, [isValid, errors, mode, editJob, setValue]);

    const role = useWatch({ control, name: "role" })
    const locationId = useWatch({ control, name: "location" })
    const from = useWatch({ control, name: "from" })
    const to = useWatch({ control, name: "to" })
    const startDate = useWatch({ control, name: "startDate" }) as Dayjs
    const instaBook = useWatch({ control, name: "instaBook" })

    const instabookDisabled = !selectedLocation?.stripeCustomerId || !selectedLocation?.isCardValid

    useEffect(() => {
        getLocations({})
    }, []);

    useEffect(() => {
        if (instaBook && instabookDisabled) {
            setUpdatePaymentModal(true)
        }
    }, [instaBook]);

    useEffect(() => {
        if (locations.length === 1)
            setValue('location', locations[0].id)
    }, [locations]);

    useEffect(() => {
        if (!lastJobLoading) {
            if (role && locationId) {
                if (lastJob?.type === Number(role)) {
                    setValue('proposedRate', lastJob?.proposedRate)
                    setValue('paymentTerms', lastJob?.paymentTerms)
                    setValue('paymentMethod', lastJob?.paymentMethod)
                } else {
                    if (isEmpty(lastJob)) {
                        setValue('proposedRate', Number(role) === Type.Hygienist ? 50 : 25)
                    } else {
                        resetField('proposedRate')
                        resetField('paymentTerms')
                        resetField('paymentMethod')
                    }
                }
            }
        }
    }, [lastJob, lastJobLoading]);

    useEffect(() => {
        if (locationId && role) {
            const location = locations.find(location => location.id === locationId)
            setSelectedLocation(location)
            resetField('instaBook')
            let type = Number(role) as Type
            getLastDentistJob({
                type,
                locationId,
            })
        }
        if (Number(role) === Type.Assistant && user?.country === Country.Canada) {
            setValue('level', 1)
        }
    }, [locationId, role])
    
    const paymentTermsValue = watch('paymentTerms')
    const paymentMethodValue = watch('paymentMethod')
    
    useEffect(() => {
        if (selectedLocation?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST) {
            if (paymentTermsValue !== 1) {
                setValue('paymentTerms', 1)
            }
            if (paymentMethodValue !== 1) {
                setValue('paymentMethod', 1)
            }
        }
    }, [paymentTermsValue, paymentMethodValue, selectedLocation])

    const postedTreeMonthsFromNow = dayjs(startDate) > dayjs().add(3, 'months')

    const onSubmit = async (data: any) => {
        console.log('Form errors:', errors);
        if (Object.keys(errors).length === 0) {
            setData(data)
            setIsJobReadyModal(true)
        } else {
            console.log('Form validation failed:', errors);
        }
    }

    const onPostShift = async (data: any) => {
        setIsJobReadyModal(false)

        setLoader(true)

        const startDate = dayjs(data.startDate).format('YYYY-MM-DD')
        const postedStart = dayjs(data.from).format('YYYY-MM-DD HH:mm')
        const postedEnd = dayjs(data.to).format('YYYY-MM-DD HH:mm')

        let jobData: JobDataProps = {
            job: {
                startDate: startDate,
                status: JOB_STATUSES.POSTED,
                type: Number(data.role),
                isOrtho: data.skills === 'isOrthodonticFlag' ? 1 : 0,
                isRestorative: data.skills === 'isRestorativeFlag' ? 1 : 0,
                paymentTerms: data.paymentTerms,
                paymentMethod: data.paymentMethod,
                locationId: data.location,
                requiredLevel: data?.level,
                requiredN95: 0,
                Instabook: data.instaBook,
                proposedRate: data.proposedRate,
                paysW2: data?.paysW2,
            },
            shifts: [
                {
                    shiftDate: startDate,
                    postedStart: postedStart,
                    postedEnd: postedEnd,
                    type: Number(data.role),
                    gapUnpaid: data.downtimeUnpaid ? 1 : 0,
                    remindStatus: 1
                }
            ],
            ppe: {}
        }

        try {
            const postedJob = await Api.postRegularJob(jobData);
            afterPostJobActions(postedJob, jobData, country)
            setJobId(postedJob.jobId)
            getAvailableHygienists({ jobId: postedJob.jobId })
        } catch (e: any) {
            console.log('VALIDATION ERRORS', errors, 'POST ERRORS', e);
            setLoader(false)
            setError(e?.message ? JSON.parse(e.message)?.message : 'An error occurred')
        }
    }

    const closeIsJobPostedModal = async () => {
        setIsJobPostedModal(false)

        if (selectedLocation?.id) {
            try {
                const getAllLocationJobs = await Api.getLocationJobs(selectedLocation?.id);
                const isFirstJob = getAllLocationJobs.length < 2;
                if (isFirstJob && selectedLocation?.stripeCustomerId === null && selectedLocation?.isCentralBooked !== LOCATION_IS_CENTRAL_BOOKED._123_DENTIST) {
                    setIsOpenPaymentDetailsPopup(true);
                } else {
                    if (availableHygienists?.length) {
                        exitReact('job-available', { hygienists: availableHygienists, jobId: jobId })
                    } else {
                        exitReact('home')
                    }
                    history.push('loading')
                }
            } catch (e) {
                console.error('Error fetching location jobs:', e);
            }
        }
    }

    const goUpdatePaymenInfo = () => {
        history.push('loading')
        exitReact('payment-info', { locationId: selectedLocation?.id })
    }

    const timePickerOpts = timePickerOptions(startDate?.toDate(), 5, 23, 15)

    return (
        selectedLocation && data && isJobReadyModal
            ? (<PostJobConfirmation
                shift={data}
                onPostShift={() => onPostShift(data)}
                onEditShift={() => setIsJobReadyModal(false)}
                location={selectedLocation as Location}
                from={from}
                to={to}
                role={Number(role) as Type}
            />)
            : (<>
                <TopBarRedesigned
                    onBack={() => {
                        history.push('loading')
                        exitReact('home')
                    }}
                    title={'Find a Temp'}
                />

                <SimpleModal isOpen={!!error.length}>
                    <ErrorModal message={error} onCloseButtonClick={() => setError('')} />
                </SimpleModal>

                {selectedLocation && from && to && jobId && <SimpleModal isOpen={isJobPostedModal}>
                    <ShiftIsLiveModal
                        availableHygienists={availableHygienists}
                        onCloseButtonClick={closeIsJobPostedModal}
                        location={selectedLocation as Location}
                        from={from}
                        to={to}
                        role={Number(role) as Type}
                    />
                </SimpleModal>}

                <Popup opened={isUpdatePaymentModal} onClose={() => setUpdatePaymentModal(false)}>
                    <UpdatePaymentInformationModal
                        onDontEnableInstabook={() => {
                            setValue('instaBook', false)
                            setUpdatePaymentModal(false)
                        }}
                        updatePaymentInfo={goUpdatePaymenInfo}
                    />
                </Popup>

                {selectedLocation?.id && <Popup opened={isOpenPaymentDetailsPopup} onClose={() => setIsOpenPaymentDetailsPopup(false)}>

                    <AddPaymentDetailsModal
                        setIsOpenPaymentDetailsPopup={setIsOpenPaymentDetailsPopup}
                        locationId={selectedLocation?.id}
                    />
                </Popup>
                }

                <Container className={styles.container}>
                    <form onSubmit={handleSubmit(onSubmit)}>

                        <div className={styles.tempAmount}>
                            <div className={styles.tempAmountText}>
                                <MonetizationOnOutlinedIcon className={styles.coinIcon} />
                                <b>{` $${Number(user?.dentist?.tempAmount) / 100}`}</b>
                                {` per shift` + (country ? '' : ' (+HST)')}
                            </div>
                            <div className={styles.tempAmountValue}>Our flat fee is only ever applied after a shift is
                                completed.
                            </div>
                        </div>

                        <Container className={styles.typeSelect}>
                            <FormRadioInputGroup name="role" control={control}>
                                {roles.map(({ value, text }) =>
                                    <RadioInput
                                        name={value}
                                        key={value}
                                        value={value}
                                        label={text}
                                    />)
                                }
                            </FormRadioInputGroup>
                        </Container>

                        <div className={clsx(styles.inputContainer)}>
                            <FormMaterialSelector
                                control={control}
                                name="location"
                                label="Office Location"
                            >
                                {locations.map((location) => <MenuItem
                                    key={location.id}
                                    value={location.id}
                                >
                                    {formatFullLocation(location)}
                                </MenuItem>)}
                            </FormMaterialSelector>
                        </div>

                        <FormMaterialCalendarPicker
                            className={styles.styledCalendarPicker}
                            name={'startDate'}
                            label={'Shift Date'}
                            control={control}
                        />

                        {postedTreeMonthsFromNow && <div className={styles.warningBox}>
                            <div className={styles.warningBoxText}>
                                <ReportProblemOutlinedIcon className={styles.warningIcon} />
                                <b>Please note</b>
                            </div>
                            <div className={styles.warningBoxValue}>
                                Shifts confirmed far in advance have a much higher likelihood of being cancelled. Please
                                consider posting your shifts less than 3 months in advance.
                            </div>
                        </div>}

                        <div className={clsx(styles.inputContainer, styles.timePickers)}>
                            <div className={styles.timePickerBox}>
                                <FormMaterialSelector control={control} name={'from'} label={'Arrival Time'}>
                                    {[
                                        <MenuItem key={'selectArrivalTime'} value={''}>Select Arrival Time</MenuItem>,
                                        ...timePickerOpts
                                    ]}
                                </FormMaterialSelector>
                                <div className={styles.helperText}>
                                    "Arrival Time" is the time you want your temp changed into scrubs and ready to start work.
                                    If you want them in for training/orientation, adjust accordingly.
                                </div>
                            </div>
                            <div className={styles.timePickerBox}>
                                <FormMaterialSelector control={control} name={'to'} label={'End Time'}>
                                    {[
                                        <MenuItem key={'selectEndingTime'} value={''}>Select Ending Time</MenuItem>,
                                        ...timePickerOpts
                                    ]}
                                </FormMaterialSelector>
                                <div className={styles.helperText}>
                                    "End Time" is the time the shift is expected to end - considering last patient, clean-up
                                    time and clinical notes.
                                </div>
                            </div>
                        </div>

                        <div className={styles.inputContainer}>
                            <FormMaterialSelector
                                className={styles.styledSelector}
                                control={control}
                                name="proposedRate"
                                label="Proposed $/hr Rate"
                            >
                                {rateOptions(Number(role) as Type, lastJob?.proposedRate)}
                            </FormMaterialSelector>
                            <div className={styles.helperText}>
                                This sets the hourly rate range a temp sees when submitting their offer.
                            </div>
                        </div>

                        <div className={styles.inputContainer}>
                            <FormMaterialSelector
                                className={styles.styledSelector}
                                control={control}
                                name="paymentTerms"
                                label="Payment Terms"
                            >
                                {selectedLocation?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST ? 
                                    paymentTermsOpts123Dentist.map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>) 
                                : 
                                    paymentTermsOpts.map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>)
                                }
                            </FormMaterialSelector>
                            <div className={styles.helperText}>
                                This is the date by which you agree to pay your temp's invoice.
                                {selectedLocation?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST && 
                                    " For 123Dentist locations, payment terms are fixed."}
                            </div>
                        </div>

                        <div className={styles.inputContainer}>
                            <FormMaterialSelector
                                className={styles.styledSelector}
                                control={control}
                                name="paymentMethod"
                                label="Payment Method"
                            >
                                {selectedLocation?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST ?
                                    paymentMethodOpts(country, true).map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>) :
                                    paymentMethodOpts(country, false).map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>)}
                            </FormMaterialSelector>
                            <div className={styles.helperText}>
                                This is the method of payment by which you expect to pay your temp's invoice.
                                {selectedLocation?.isCentralBooked === LOCATION_IS_CENTRAL_BOOKED._123_DENTIST && 
                                    " For 123Dentist locations, payment method is fixed."}
                            </div>
                        </div>

                        {country ? <div className={clsx(styles.inputContainer, styles.w2Container)}>
                            <FormMaterialSelector
                                className={styles.styledSelector}
                                control={control}
                                name="paysW2"
                                label="Working relationship"
                            >
                                <MenuItem key={'payment-any'} value={2}>As either W2 or 1099</MenuItem>
                                <MenuItem key={'payment-w2'} value={1}>As W2 Temporary Employee</MenuItem>
                                <MenuItem key={'payment-1099'} value={0}>As 1099 Independent Contractor</MenuItem>
                            </FormMaterialSelector>
                            <a id="item-w2-questions" className="external" target="_system"
                                href="https://www.tempstars.com/1099-w2/">
                                <InfoOutlinedIcon />
                            </a>
                        </div> : null}

                        {Number(role) === Type.Hygienist ? <div className={styles.inputContainer}>
                            <FormCheckboxInput
                                className={styles.checkboxContainer}
                                name={'downtimeUnpaid'}
                                label={'Downtime unpaid'}
                                control={control}
                            />
                            <div className={clsx(styles.helperText, styles.downtimeUnpaidInfo)}>
                                Most offices pay for down time (schedule gaps and patient cancellations during the day).
                                If you don't, or not at full rate, check this box.
                                This is a bad idea - it significantly reduces the number of offers you'll receive.
                            </div>
                        </div> : null}

                        {Number(role) === Type.Assistant
                            && country === Country.Canada
                            && selectedLocation?.province === "ON"
                            ? <div className={styles.inputContainer}>
                                <FormMaterialSelector
                                    className={styles.styledSelector}
                                    control={control}
                                    name="level"
                                    label="DA Level Required?"
                                >
                                    <MenuItem key={'cda1'} value={1}>CDA Level I or Level II</MenuItem>
                                    <MenuItem key={'cda2'} value={2}>CDA Level II Required</MenuItem>
                                </FormMaterialSelector>
                            </div>
                            : null}

                        {Number(role) === Type.Hygienist ? <CollapsableContainer title='Additional Skills Required?'>
                            <FormRadioInputGroup name="skills" control={control}>
                                <RadioInput
                                    name={'isOrthodonticFlag'}
                                    key={'isOrthodonticFlag'}
                                    value={'isOrthodonticFlag'}
                                    label={'Orthodontic'}
                                />
                                <RadioInput
                                    name={'isRestorativeFlag'}
                                    key={'isRestorativeFlag'}
                                    value={'isRestorativeFlag'}
                                    label={'Restorative'}
                                />
                            </FormRadioInputGroup>
                        </CollapsableContainer> : null}

                        <div className={styles.instaBookContainer}>
                            <div className={styles.leftContainer}>
                                <Switch
                                    {...register("instaBook")}
                                    disabled={!selectedLocation}
                                    checked={instaBook}
                                    name="instaBook"
                                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                                />
                                <SpeedIcon className={styles.speedIcon} />
                                <div className={styles.instaBook}>Enable Insta-Book</div>
                            </div>
                            <div className={styles.description}>
                                With this enabled, your {country === Country.USA ? 'Favorited' : 'Favourited'} Dental Professionals as well as our Pro & Elite Status users can book your shift at your proposed rate, without you needing to approve their offer.
                                <br /><br />
                                <div className={styles.bold}>
                                    This will significantly increase the chances of filling this shift quickly.
                                </div>
                            </div>
                        </div>

                        <div className={clsx(styles.inputContainer, styles.ppeContainer)}>
                            <FormCheckboxInput
                                className={styles.checkboxContainer}
                                name={'ppe'}
                                label={`Our office is compliant with ${country ? 'State' : 'Provincial'} & Federal guidelines with
                            respect to workplace safety and infection control protocols.`}
                                control={control}
                            />
                            <a href={'https://support.tempstars.com/hc/en-us/articles/360051064813-PPE-and-Infection-Control-for-Temping-Shifts'} target="_system" className={"external"}>
                                <InfoOutlinedIcon />
                            </a>
                        </div>

                        <div className={styles.bottomContainer}>
                            <Button
                                type={'submit'}
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                color="primary"
                                size="large"
                                id="continue-button"
                                className="bigButtonInNewStyle"
                            >
                                Review & Confirm
                            </Button>
                            <a href={'https://www.tempstars.com/short-notice'} target="_system" className={"external"}>
                                <div className={styles.cancellationPolicy}>
                                    TempStars Cancellation Policy
                                </div>
                            </a>
                        </div>
                    </form>
                </Container>
                <FixedLoader loading={locationsLoading || loader || getAvailableHygienistsLoading} />
            </>))
}

export default PostJob;